import type { DefaultError, QueryObserverResult } from '@tanstack/query-core'
import { atom, useAtomValue } from 'jotai'
import type { Atom } from 'jotai'
import { atomWithQuery } from 'jotai-tanstack-query'
import { atomFamily } from 'jotai/utils'
import { useEffect, useRef } from 'react'
import isEqual from 'react-fast-compare'
import type { SearchCriteria } from '~/features/search'
import {
  appliedSearchCriteria$,
  draftLargeAreaCode$,
  draftMediumAreaCode$,
  draftSearchCriteria$,
} from '~/features/search'
import type { CityId, LargeAreaCode, MediumAreaCode } from '~/lib/graphql/scalars'
import { useApplied$ } from './applied'
import type {
  City,
  FacetHitCounts,
  Genre,
  Genres,
  LargeArea,
  MajorGenre,
  MediumArea,
  Spot,
  Town,
} from './types'

// エリアや市区町村・ジャンルは静的な json を返しているだけなので revalidate 不要
const day = 1000 * 60 * 60 * 24

const largeAreas$ = atomWithQuery<readonly LargeArea[]>(() => ({
  queryKey: ['largeAreas'],
  queryFn: async () => {
    const response = await fetch('/api/v2/facets/areas')
    if (!response.ok) {
      throw new Error(`Failed to fetch large areas: ${response.statusText}`)
    }
    return response.json()
  },
  gcTime: day,
  staleTime: day,
}))
largeAreas$.debugLabel = 'largeAreas$'

// mediumAreaMap$ を作りたいので元となる atomWithQuery も atomFamily にする
export const mediumAreas$ = atomFamily<
  [LargeAreaCode, MediumAreaCode],
  Atom<QueryObserverResult<MediumArea, DefaultError>>
>(([largeAreaCode, mediumAreaCode]) => {
  const atom$ = atomWithQuery<MediumArea>(() => ({
    queryKey: ['mediumArea', largeAreaCode, mediumAreaCode],
    queryFn: async ({ queryKey: [_, largeAreaCode, mediumAreaCode] }): Promise<MediumArea> => {
      const response = await fetch(`/api/v2/facets/areas/${largeAreaCode}${mediumAreaCode}`)
      if (!response.ok) {
        throw new Error(`Failed to fetch medium area: ${response.statusText}`)
      }
      return response.json()
    },
    enabled:
      largeAreaCode !== '00' &&
      largeAreaCode !== undefined &&
      mediumAreaCode !== '000' &&
      mediumAreaCode !== undefined,
    gcTime: day,
    staleTime: day,
  }))
  atom$.debugLabel = `mediumAreas$-${largeAreaCode}${mediumAreaCode}`
  return atom$
}, isEqual)

export const mediumArea$ = atom<MediumArea | undefined>((get) => {
  const largeAreaCode = get(draftLargeAreaCode$)
  if (largeAreaCode === undefined) {
    return undefined
  }
  const mediumAreaCode = get(draftMediumAreaCode$)
  if (mediumAreaCode === undefined) {
    return undefined
  }
  return get(mediumAreas$([largeAreaCode, mediumAreaCode])).data
})

export const cities$ = atomWithQuery<City[]>((get) => {
  const largeAreaCode = get(draftLargeAreaCode$)
  const mediumAreaCode = get(draftMediumAreaCode$)
  return {
    queryKey: ['cities', largeAreaCode, mediumAreaCode],
    queryFn: async ({ queryKey: [_, largeAreaCode, mediumAreaCode] }): Promise<City[]> => {
      const response = await fetch(`/api/v2/facets/cities/${largeAreaCode}${mediumAreaCode}`)
      if (!response.ok) {
        throw new Error(`Failed to fetch cities: ${response.statusText}`)
      }
      return response.json()
    },
    enabled: largeAreaCode !== undefined && mediumAreaCode !== undefined,
    gcTime: day,
    staleTime: day,
  }
})
cities$.debugLabel = 'cities$'

const spotQuery$ = atomWithQuery<Spot>((get) => {
  const spotId = get(draftSearchCriteria$)?.spotId
  return {
    queryKey: ['spots', spotId],
    queryFn: async ({ queryKey: [_, spotId] }): Promise<Spot> => {
      const response = await fetch(`/api/v2/facets/spots/${spotId}`)
      return response.json()
    },
    enabled: spotId !== undefined,
    gcTime: day,
    staleTime: day,
  }
})
spotQuery$.debugLabel = 'spotQuery$'

export const spot$ = atom((get) => get(spotQuery$).data)
spot$.debugLabel = 'spot$'

// 同時に複数開くので atomFamily で同時に値を持てるように
export const towns$ = atomFamily<CityId, Atom<QueryObserverResult<Town[], DefaultError>>>(
  (cityId) => {
    const atom$ = atomWithQuery<Town[]>((_get) => ({
      queryKey: ['town', cityId],
      queryFn: async ({ queryKey: [_prefix, cityId] }): Promise<Town[]> => {
        const response = await fetch(`/api/v2/facets/towns/${cityId}`)
        return response.json()
      },
      gcTime: day,
      staleTime: day,
    }))
    atom$.debugLabel = `towns$-${cityId}`
    return atom$
  }
)

export function useCities() {
  return useAtomValue(cities$).data
}

export function useTowns(cityId: CityId) {
  return useAtomValue(towns$(cityId)).data
}

const genres$ = atomWithQuery<Genres>(() => ({
  queryKey: ['genres'],
  queryFn: async () => {
    const response = await fetch('/api/v2/facets/genres')
    return response.json()
  },
  gcTime: day,
  staleTime: day,
}))
genres$.debugLabel = 'genres$'

export const genreMap$ = atom<Map<string, Genre>>((get) => {
  const genres = get(genres$).data?.flatMap(flattenGenre) ?? []
  return new Map(genres.map((genre) => [genre.code, genre]))
})
genreMap$.debugLabel = 'genreMap$'

export type GenreMap = typeof genreMap$

export function flattenGenre(major: MajorGenre): readonly Genre[] {
  return [major, ...major.minors]
}

export function useLargeAreas() {
  return useAtomValue(largeAreas$).data
}

export function useMediumArea() {
  return useAtomValue(mediumArea$)
}

export function useGenres() {
  return useAtomValue(genres$).data
}

type QueryKey = ['facet-hit-counts', SearchCriteria | undefined]

function facetHitCountsQueryOptions(criteria: SearchCriteria | undefined) {
  async function queryFn({
    queryKey: [_, criteria],
  }: { queryKey: QueryKey }): Promise<FacetHitCounts> {
    const qs = new URLSearchParams({ q: JSON.stringify(criteria) })
    const response = await fetch(`/api/v2/facets/hit-counts?${qs}`)
    return response.json()
  }
  return {
    queryKey: ['facet-hit-counts', criteria] as QueryKey,
    queryFn,
    enabled: criteria !== undefined,
  }
}

const draftFacetHitCounts$ = atomWithQuery((get) => {
  return facetHitCountsQueryOptions(get(draftSearchCriteria$))
})
draftFacetHitCounts$.debugLabel = 'facetHitCounts$'

const appliedFacetHitCounts$ = atomWithQuery((get) => {
  return facetHitCountsQueryOptions(get(appliedSearchCriteria$))
})
appliedFacetHitCounts$.debugLabel = 'appliedFacetHitCounts$'

const facetHitCounts$ = atom((get) => {
  const applied = get(useApplied$)
  return get(applied ? appliedFacetHitCounts$ : draftFacetHitCounts$).data
})
facetHitCounts$.debugLabel = 'facetHitCounts$'

export function useFacetHitCounts() {
  return useAtomValue(facetHitCounts$)
}

export function useFacetHitCount(...keys: string[]) {
  const counts = useFacetHitCounts()
  if (counts === undefined) {
    return undefined
  }
  return keys.reduce((acc, key) => acc + (counts?.[key] ?? 0), 0)
}

export function useFacetDisabled(key: string) {
  const count = useFacetHitCount(key)
  const prev = useRef(count)
  useEffect(() => {
    if (count !== undefined) {
      prev.current = count
    }
  }, [count])
  return (
    count === 0 ||
    // 0 -> undefined -> 0 で一瞬 active になってしまうのを防ぐ
    (prev.current === 0 && count === undefined)
  )
}
